import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronsRight, SquarePen, Clock4 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import SingleFileChat, { type SingleFileChatRef } from './single-file';
import MultiFileChat, { type MultiFileChatRef } from './multi-file';
import type { FileType } from '../../types';
import type { RootState } from '../../reducers';

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
  width: number;
  onWidthChange: (width: number) => void;
  onDragWidthChange?: (width: number | null) => void;
  shouldAnimate?: boolean;
  onOpen?: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  open,
  onClose,
  width,
  onWidthChange,
  onDragWidthChange,
  shouldAnimate = false,
  onOpen,
}) => {
  const location = useLocation();
  const [isDragging, setIsDragging] = React.useState(false);
  const [isHoveringHandle, setIsHoveringHandle] = React.useState(false);
  const dragStateRef = React.useRef({
    startX: 0,
    initialWidth: 400,
    currentWidth: 400,
  });
  const animationFrameRef = React.useRef<number | null>(null);
  const multiFileChatRef = React.useRef<MultiFileChatRef>(null);
  const singleFileChatRef = React.useRef<SingleFileChatRef>(null);
  const [showHistoryDropdown, setShowHistoryDropdown] = React.useState(false);
  const [hasChatContent, setHasChatContent] = React.useState(false);

  // Track the previous folder ID to detect folder changes
  const prevFolderIdRef = React.useRef<string | null>(null);

  // Get library files from Redux
  const libraryFiles = useSelector(
    (state: RootState) => state.library.files.data,
  );

  // Check if we're in selected video view
  // Path format: /library/{teamId}/recents/{fileId} OR /library/{teamId}/{folderId}/{fileId}
  // NOT library list: /library/{teamId}/recents (only 3 parts)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isSelectedVideoView =
    pathParts[0] === 'library' && pathParts.length >= 4; // Selected video has 4 parts: library, teamId, folderId/recents, fileId

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragStateRef.current = {
      startX: e.clientX,
      initialWidth: width,
      currentWidth: width,
    };

    setIsDragging(true);
    document.documentElement.classList.add('chat-panel-dragging');
  };

  // Store open state in ref to avoid stale closures
  const openRef = React.useRef(open);
  React.useEffect(() => {
    openRef.current = open;
  }, [open]);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const dragDistance = dragStateRef.current.startX - e.clientX;
        const rawWidth = dragStateRef.current.initialWidth + dragDistance;

        const COLLAPSE_THRESHOLD = 120;
        const EXPAND_THRESHOLD = 120;

        // If currently open and dragged right (width decreasing) below threshold, trigger collapse
        if (openRef.current && rawWidth < COLLAPSE_THRESHOLD) {
          setIsDragging(false);
          document.documentElement.classList.remove('chat-panel-dragging');
          document.documentElement.style.removeProperty(
            '--chat-panel-resize-width',
          );
          onDragWidthChange?.(null);
          onClose();
          return;
        }

        // If currently closed and dragged left (width increasing) past threshold, trigger expand
        if (!openRef.current && rawWidth > EXPAND_THRESHOLD) {
          setIsDragging(false);
          document.documentElement.classList.remove('chat-panel-dragging');
          document.documentElement.style.removeProperty(
            '--chat-panel-resize-width',
          );
          onDragWidthChange?.(null);
          onOpen?.();
          return;
        }

        // Calculate maximum width to ensure middle column stays at least 33vw
        // Available space = viewport width - sidebar width (or collapsed width)
        const viewportWidth = window.innerWidth;
        const sidebarElement = document.querySelector(
          '[data-sidebar="sidebar"]',
        );
        const sidebarWidth =
          sidebarElement?.getBoundingClientRect().width || 56; // Default to collapsed width
        const minMiddleColumnWidth = viewportWidth * 0.33; // 33vw
        const maxChatWidth =
          viewportWidth - sidebarWidth - minMiddleColumnWidth;

        const newWidth = Math.max(
          300,
          Math.min(Math.min(800, maxChatWidth), rawWidth),
        );

        dragStateRef.current.currentWidth = newWidth;
        document.documentElement.style.setProperty(
          '--chat-panel-resize-width',
          `${newWidth}px`,
        );

        // Notify parent of drag width change for synchronous margin updates
        onDragWidthChange?.(newWidth);
      });
    },
    [onDragWidthChange, onClose, onOpen],
  );

  const isDraggingRef = React.useRef(isDragging);
  React.useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  const handleMouseUp = React.useCallback(() => {
    if (!isDraggingRef.current) return;

    const finalWidth = dragStateRef.current.currentWidth;
    setIsDragging(false);
    document.documentElement.classList.remove('chat-panel-dragging');
    document.documentElement.style.removeProperty('--chat-panel-resize-width');

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clear drag width state
    onDragWidthChange?.(null);

    // Calculate maximum width to ensure middle column stays at least 33vw
    const viewportWidth = window.innerWidth;
    const sidebarElement = document.querySelector('[data-sidebar="sidebar"]');
    const sidebarWidth = sidebarElement?.getBoundingClientRect().width || 56;
    const minMiddleColumnWidth = viewportWidth * 0.33;
    const maxChatWidth = viewportWidth - sidebarWidth - minMiddleColumnWidth;

    const constrainedWidth = Math.max(
      300,
      Math.min(Math.min(800, maxChatWidth), finalWidth),
    );
    onWidthChange(constrainedWidth);
  }, [onWidthChange, onDragWidthChange]);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove, { capture: true });
      window.addEventListener('mouseup', handleMouseUp, { capture: true });

      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      (document.body.style as any).webkitUserSelect = 'none';
      document.body.style.pointerEvents = 'none';

      const handle = document.querySelector(
        '[data-testid="chat-panel-resize-handle"]',
      ) as HTMLElement;
      if (handle) {
        handle.style.pointerEvents = 'auto';
      }
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove, {
        capture: true,
      });
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      (document.body.style as any).webkitUserSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Check if chat has content - fast polling for instant icon switching
  React.useEffect(() => {
    const checkContent = () => {
      const hasContent = isSelectedVideoView
        ? (singleFileChatRef.current?.hasContent() ?? false)
        : (multiFileChatRef.current?.hasContent() ?? false);
      setHasChatContent(hasContent);
    };

    // Check immediately and then very frequently for instant response
    checkContent();
    const interval = setInterval(checkContent, 50); // Check every 50ms for instant updates

    return () => clearInterval(interval);
  }, [isSelectedVideoView]);

  // Auto-select files when switching to a folder (not recents)
  React.useEffect(() => {
    // Only proceed if we're in the library list view (not selected video view)
    if (isSelectedVideoView) return;

    // Extract folderId from URL path
    // Path format: /library/{teamId}/{folderId}
    const urlParts = location.pathname.split('/').filter(Boolean);
    const currentFolderId =
      urlParts[0] === 'library' && urlParts.length >= 3 ? urlParts[2] : null;

    // Check if we're in a folder (not recents)
    const isInFolder = currentFolderId && currentFolderId !== 'recents';
    const hasFiles = libraryFiles && libraryFiles.length > 0;
    const chatIsEmpty = !hasChatContent;

    // Filter files to only include those that match the current folder
    // Files have a parentId property that matches the folder they belong to
    const filesInCurrentFolder = hasFiles
      ? libraryFiles.filter((file) => file.parentId === currentFolderId)
      : [];

    // Check if folder has changed AND files have loaded for the new folder
    const folderChanged =
      currentFolderId && currentFolderId !== prevFolderIdRef.current;
    const filesMatchFolder = filesInCurrentFolder.length > 0;

    console.log('[ChatPanel] Auto-select check:', {
      currentFolderId,
      prevFolderId: prevFolderIdRef.current,
      isInFolder,
      folderChanged,
      hasFiles,
      filesMatchFolder,
      chatIsEmpty,
      libraryFilesCount: libraryFiles?.length,
      matchingFilesCount: filesInCurrentFolder.length,
      firstFileParentId: libraryFiles?.[0]?.parentId,
    });

    // Auto-select files when switching folders:
    // 1. We're in a folder (not recents)
    // 2. The folder has changed (different from previous)
    // 3. Files have loaded that match the current folder
    if (isInFolder && folderChanged && filesMatchFolder) {
      console.log(
        '[ChatPanel] Folder changed, clearing old files and adding new:',
        filesInCurrentFolder.map((f) => f.name),
      );

      // Clear the chat first to remove old folder's files
      multiFileChatRef.current?.clearChat();

      // Add the new folder's files
      filesInCurrentFolder.forEach((file) => {
        multiFileChatRef.current?.addFile(file);
      });

      // Update the previous folder ID ONLY after successfully adding files
      prevFolderIdRef.current = currentFolderId;
    } else if (!folderChanged && currentFolderId) {
      // If folder hasn't changed but we have a current folder, keep ref in sync
      prevFolderIdRef.current = currentFolderId;
    }
  }, [location.pathname, isSelectedVideoView, libraryFiles, hasChatContent]);

  // Auto-focus input when panel opens
  React.useEffect(() => {
    if (!open) return undefined;

    // Use setTimeout to ensure the panel has fully opened and rendered
    const timeoutId = setTimeout(() => {
      if (isSelectedVideoView) {
        singleFileChatRef.current?.focusInput();
      } else {
        multiFileChatRef.current?.focusInput();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [open, isSelectedVideoView]);

  // Expose chat context methods globally for library file selection
  React.useEffect(() => {
    (window as any).__chatContextControls = {
      addFileToContext: (file: FileType) => {
        // Only add to MultiFileChat (multi-file chat), not to SingleFileChat
        if (!isSelectedVideoView && multiFileChatRef.current?.addFile) {
          multiFileChatRef.current.addFile(file);
        }
      },
      removeFileFromContext: (fileId: string) => {
        // Only remove from MultiFileChat (multi-file chat), not to SingleFileChat
        if (!isSelectedVideoView && multiFileChatRef.current?.removeFile) {
          multiFileChatRef.current.removeFile(fileId);
        }
      },
    };

    return () => {
      delete (window as any).__chatContextControls;
    };
  }, [isSelectedVideoView]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const currentWidth = isDragging
    ? `var(--chat-panel-resize-width, ${width}px)`
    : `${width}px`;

  // Mobile: Use Sheet component for slide-over with backdrop
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent
          side="right"
          className="w-[90vw] sm:max-w-md p-0 [&>button]:hidden"
        >
          {/* Header */}
          <div className="h-14 flex items-center justify-between pr-3 pl-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <ChevronsRight className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
              <h2 className="text-base font-semibold">Chat</h2>
            </div>
            <div className="flex items-center gap-2 relative -ml-2">
              {!isSelectedVideoView && !hasChatContent && (
                <button
                  type="button"
                  onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  title="View history"
                  data-history-trigger
                >
                  <Clock4 className="h-4 w-4" />
                </button>
              )}
              {hasChatContent && (
                <button
                  type="button"
                  onClick={() => {
                    if (isSelectedVideoView) {
                      singleFileChatRef.current?.clearChat();
                    } else {
                      multiFileChatRef.current?.clearChat();
                    }
                  }}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  title="New chat"
                >
                  <SquarePen className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col h-[calc(100vh-3.5rem)]">
            {isSelectedVideoView ? (
              <div className="flex-1 flex flex-col h-full">
                <SingleFileChat ref={singleFileChatRef} isInPanel />
              </div>
            ) : (
              <div className="flex-1 flex flex-col h-full relative">
                <MultiFileChat
                  open
                  ref={multiFileChatRef}
                  externalShowHistoryDropdown={showHistoryDropdown}
                  externalSetShowHistoryDropdown={setShowHistoryDropdown}
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Use fixed panel with resize handle
  return (
    <>
      <style>
        {`
          .chat-panel-dragging * {
            transition: none !important;
          }
        `}
      </style>
      <div
        className={cn(
          'fixed top-0 h-full bg-white dark:bg-secondary border-l border-border flex flex-col z-40',
          shouldAnimate && 'transition-transform duration-100 ease-out',
        )}
        style={{
          width: currentWidth,
          right: 0,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Resize handle on left edge */}
        <button
          type="button"
          tabIndex={0}
          className="absolute left-0 top-0 w-2 h-full cursor-ew-resize group border-0 bg-transparent p-0 outline-none focus-visible:outline-none z-50"
          onMouseDown={handleResizeMouseDown}
          onMouseEnter={() => setIsHoveringHandle(true)}
          onMouseLeave={() => setIsHoveringHandle(false)}
          aria-label="Resize chat panel"
          data-testid="chat-panel-resize-handle"
          onKeyDown={(e) => {
            // Allow keyboard resizing with arrow keys
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              const viewportWidth = window.innerWidth;
              const sidebarElement = document.querySelector(
                '[data-sidebar="sidebar"]',
              );
              const sidebarWidth =
                sidebarElement?.getBoundingClientRect().width || 56;
              const minMiddleColumnWidth = viewportWidth * 0.33;
              const maxChatWidth =
                viewportWidth - sidebarWidth - minMiddleColumnWidth;
              const newWidth = Math.min(
                Math.min(800, maxChatWidth),
                width + 10,
              );
              onWidthChange(newWidth);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              const newWidth = Math.max(300, width - 10);
              onWidthChange(newWidth);
            }
          }}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 w-1 transition-all duration-150',
              {
                'bg-blue-500 opacity-100': isDragging,
                'bg-gray-300 dark:bg-gray-600 opacity-60':
                  !isDragging && isHoveringHandle,
                'opacity-0': !isDragging && !isHoveringHandle,
              },
            )}
          />
        </button>

        {/* Header */}
        <div className="h-14 flex items-center justify-between pr-3 pl-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <ChevronsRight className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            <h2 className="text-base font-semibold">Chat</h2>
          </div>
          <div className="flex items-center gap-2 relative -ml-2">
            {!isSelectedVideoView && !hasChatContent && (
              <button
                type="button"
                onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                title="View history"
                data-history-trigger
              >
                <Clock4 className="h-4 w-4" />
              </button>
            )}
            {hasChatContent && (
              <button
                type="button"
                onClick={() => {
                  if (isSelectedVideoView) {
                    singleFileChatRef.current?.clearChat();
                  } else {
                    multiFileChatRef.current?.clearChat();
                  }
                }}
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                title="New chat"
              >
                <SquarePen className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isSelectedVideoView ? (
            <div className="flex-1 flex flex-col h-full">
              <SingleFileChat ref={singleFileChatRef} isInPanel />
            </div>
          ) : (
            <div className="flex-1 flex flex-col h-full relative">
              <MultiFileChat
                open
                ref={multiFileChatRef}
                externalShowHistoryDropdown={showHistoryDropdown}
                externalSetShowHistoryDropdown={setShowHistoryDropdown}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatPanel;
